import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CatalogService } from '@proxy/catalogs/catalog.service';
import { WineDto } from '@proxy/wines/models';
import { SommelierService } from '@proxy/sommeliers';
import { WineryService } from '@proxy/wineries';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class ImagePreloadService {
  private preloadedImages = new Set<string>();
  private cache = new Map<string, string>();


  constructor(
    private catalogService: CatalogService,
    private sommelierService: SommelierService,
    private wineryService: WineryService,
    private storageService: StorageService
  ) {}

  getCachedImage(fileAttachmentId: number): string | null {
    return this.cache.get(`wine-image-${fileAttachmentId}`) || null;
  }

  /**
   * Preloads all wine images for the catalog
   */
  preloadWineImages(sommelierSlug: string): Observable<void> {
    // First get all wines
    return this.getAllWines(sommelierSlug).pipe(
      switchMap(wines => {
        // Filter wines with fileAttachmentId to preload
        const winesWithImages = wines.filter(wine => wine.fileAttachmentId);

        if (winesWithImages.length === 0) {
          return of(undefined);
        }

        // Create observables for each image to be preloaded
        const imageRequests = winesWithImages.map(wine =>
          this.preloadImage(wine.fileAttachmentId!)
        );

        // Execute all image requests in parallel
        return forkJoin(imageRequests).pipe(
          map(() => undefined),
          catchError(error => {
            console.error('Error preloading images:', error);
            return of(undefined);
          })
        );
      })
    );
  }

  /**
   * Preloads images for wineries
   */
  preloadWineryImages(): Observable<void> {
    // Get all wineries with pagination
    return this.wineryService.getList({ skipCount: 0, maxResultCount: 1000 }).pipe(
      switchMap(result => {
        const wineriesWithImages = result.items.filter(winery => winery.imageUrl);

        if (wineriesWithImages.length === 0) {
          return of(undefined);
        }

        const imageRequests = wineriesWithImages.map(winery =>
          this.preloadExternalImage(winery.imageUrl!)
        );

        return forkJoin(imageRequests).pipe(
          map(() => undefined),
          catchError(error => {
            console.error('Error preloading winery images:', error);
            return of(undefined);
          })
        );
      })
    );
  }

  /**
   * Preloads images for sommeliers
   */
  preloadSommelierImages(): Observable<void> {
    // Get all sommeliers with pagination
    return this.sommelierService.getList({ skipCount: 0, maxResultCount: 1000 }).pipe(
      switchMap(result => {
        const sommeliersWithImages = result.items.filter(sommelier => sommelier.imageUrl);

        if (sommeliersWithImages.length === 0) {
          return of(undefined);
        }

        const imageRequests = sommeliersWithImages.map(sommelier =>
          this.preloadExternalImage(sommelier.imageUrl!)
        );

        return forkJoin(imageRequests).pipe(
          map(() => undefined),
          catchError(error => {
            console.error('Error preloading sommelier images:', error);
            return of(undefined);
          })
        );
      })
    );
  }

  /**
   * Get all wines (paginated) for the given sommelier
   */
  private getAllWines(sommelierSlug: string): Observable<WineDto[]> {
    const pageSize = 50; // Adjust as needed
    let allWines: WineDto[] = [];
    let currentPage = 0;

    const loadPage = (page: number): Observable<WineDto[]> => {
      return this.catalogService.getWinesPaged({
        skipCount: page * pageSize,
        maxResultCount: pageSize,
        sommelierSlug: sommelierSlug
      }).pipe(
        switchMap(result => {
          allWines = [...allWines, ...result.items];

          // If we received less items than requested, we've reached the end
          if (result.items.length < pageSize) {
            return of(allWines);
          }

          // Otherwise, load next page
          return loadPage(page + 1);
        })
      );
    };

    return loadPage(currentPage);
  }

  private preloadImage(fileAttachmentId: number): Observable<void> {
  const cacheKey = `wine-image-${fileAttachmentId}`;

  if (this.preloadedImages.has(cacheKey)) {
    return of(undefined);
  }

  return this.storageService.getImage(fileAttachmentId, 100, 200).pipe(
    switchMap(blob => {
      return new Observable<void>(observer => {
        const url = URL.createObjectURL(blob);

        const img = new Image();
        img.src = url;

        img.onload = () => {
          this.preloadedImages.add(cacheKey);
          this.cache.set(cacheKey, url);
          observer.next();
          observer.complete();
        };

        img.onerror = () => {
          console.warn(`Error preloading image ${fileAttachmentId}`);
          URL.revokeObjectURL(url);
          observer.next();
          observer.complete();
        };
      });
    }),
    catchError(err => {
      console.error(`Error preloading image ${fileAttachmentId}`, err);
      return of(undefined);
    })
  );
}


  /**
   * Preloads an external image URL
   */
  private preloadExternalImage(imageUrl: string): Observable<void> {
    const cacheKey = `external-image-${imageUrl}`;

    // Skip if already preloaded
    if (this.preloadedImages.has(cacheKey)) {
      return of(undefined);
    }

    return new Observable(observer => {
      const img = new Image();
      img.onload = () => {
        // Mark as preloaded
        this.preloadedImages.add(cacheKey);
        observer.next(undefined);
        observer.complete();
      };
      img.onerror = (error) => {
        console.error(`Error preloading external image ${imageUrl}:`, error);
        observer.next(undefined);
        observer.complete();
      };
      img.src = imageUrl;
    });
  }

  /**
   * Clear the preload cache
   */
  clearCache(): void {
    this.preloadedImages.clear();
  }
}
